import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, ButtonBase, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { useDispatch, useSelector } from 'store';
import { activeID, activeItem, openDrawer } from 'store/slices/menu';
import { themeColors } from 'views/ConstantColors';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const NavItem = ({ item, level, parentId }) => {
    const theme = useTheme();
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { layout, borderRadius } = useConfig();
    const { selectedItem, drawerOpen } = useSelector((state) => state.menu);

    const isSelected = selectedItem.findIndex((id) => id === item.id) > -1;

    const Icon = item?.icon;
    const itemIcon = item?.icon ? (
        <Icon
            stroke={1.5}
            size={drawerOpen ? '20px' : '24px'}
            style={{
                color: isSelected ? themeColors.primary.main : themeColors.grey[700]
            }}
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                color: isSelected ? themeColors.primary.main : themeColors.grey[700],
                width: isSelected ? 8 : 6,
                height: isSelected ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const listItemProps = item.external
        ? { component: 'a', href: item.url, target: item.target || '_blank' }
        : { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={item.target || '_self'} />) };

    const itemHandler = (id) => {
        dispatch(activeItem([id]));
        if (matchesSM) dispatch(openDrawer(false));
        dispatch(activeID(parentId));
    };

    useEffect(() => {
        const currentIndex = pathname.split('/').findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch(activeItem([item.id]));
        }
    }, [pathname]);

    return (
        <>
            <ListItemButton
                {...listItemProps}
                disabled={item.disabled}
                disableRipple={!drawerOpen}
                sx={{
                    zIndex: 1201,
                    borderRadius: `${borderRadius}px`,
                    mb: 0.5,
                    pl: drawerOpen ? `${level * 24}px` : 1.25,
                    transition: 'all 0.2s ease-in-out',
                    ...(drawerOpen &&
                        level === 1 && {
                            '&:hover': {
                                backgroundColor: themeColors.primary.lighter,
                                '& .MuiListItemIcon-root': {
                                    color: themeColors.primary.main
                                },
                                '& .MuiTypography-root': {
                                    color: themeColors.primary.main
                                }
                            },
                            '&.Mui-selected': {
                                backgroundColor: themeColors.primary.lighter,
                                color: themeColors.primary.main,
                                '&:hover': {
                                    backgroundColor: themeColors.primary.lighter,
                                    color: themeColors.primary.dark
                                }
                            }
                        }),
                    ...((!drawerOpen || level !== 1) && {
                        py: level === 1 ? 0 : 1,
                        '&:hover': {
                            bgcolor: 'transparent',
                            '& .MuiListItemIcon-root': {
                                color: themeColors.primary.main
                            },
                            '& .MuiTypography-root': {
                                color: themeColors.primary.main
                            }
                        },
                        '&.Mui-selected': {
                            bgcolor: 'transparent',
                            '&:hover': {
                                bgcolor: 'transparent'
                            }
                        }
                    })
                }}
                selected={isSelected}
                onClick={() => itemHandler(item.id)}
            >
                <ButtonBase
                    sx={{
                        borderRadius: `${borderRadius}px`,
                        transition: 'all 0.2s ease-in-out'
                    }}
                    disableRipple={drawerOpen}
                    aria-label="pages icon"
                >
                    <ListItemIcon
                        sx={{
                            minWidth: level === 1 ? 36 : 18,
                            color: isSelected ? themeColors.primary.main : themeColors.grey[700],
                            transition: 'all 0.2s ease-in-out',
                            ...(isSelected && {
                                bgcolor: themeColors.primary.lighter,
                                '&:hover': {
                                    bgcolor: themeColors.primary.lighter
                                }
                            })
                        }}
                    >
                        {itemIcon}
                    </ListItemIcon>
                </ButtonBase>

                {(drawerOpen || (!drawerOpen && level !== 1)) && (
                    <ListItemText
                        primary={
                            <Typography
                                variant={isSelected ? 'h5' : 'body1'}
                                color={isSelected ? themeColors.primary.main : themeColors.grey[700]}
                                sx={{
                                    transition: 'all 0.2s ease-in-out',
                                    fontWeight: isSelected ? 600 : 400
                                }}
                            >
                                {item.title}
                            </Typography>
                        }
                        secondary={
                            item.caption && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        ...theme.typography.subMenuCaption,
                                        color: themeColors.grey[500]
                                    }}
                                    display="block"
                                    gutterBottom
                                >
                                    {item.caption}
                                </Typography>
                            )
                        }
                    />
                )}

                {drawerOpen && item.chip && (
                    <Chip
                        color="primary"
                        variant={item.chip.variant}
                        size={item.chip.size}
                        label={item.chip.label}
                        avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                        sx={{
                            backgroundColor: themeColors.primary.main,
                            color: themeColors.primary.contrastText,
                            '& .MuiChip-label': {
                                fontSize: '0.75rem'
                            }
                        }}
                    />
                )}
            </ListItemButton>
        </>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
    parentId: PropTypes.string
};

export default NavItem;
